import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Briefcase, Users, Building2, Settings, Trash2, Check, X, BadgeCheck, RefreshCw, LogOut, Search, Grid3x3, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_ICONS } from "@/data/categories";

function AdminLogin({ onDone }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") { setErr("Bu hesap yönetici değil."); return; }
      onDone();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#1f222d] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl">
        <div className="flex justify-center mb-6"><img src="/isbul-logo.png" alt="isbul.org" className="h-12 w-auto object-contain" /></div>
        <h1 className="text-xl font-bold text-[#1f222d] text-center">Yönetici Girişi</h1>
        <p className="text-slate-500 text-sm text-center mt-1">Admin paneline erişim</p>
        {err && <div data-testid="admin-login-error" className="mt-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{err}</div>}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-posta" className="w-full border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-400 text-[#1f222d]" />
          <input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Şifre" className="w-full border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-400 text-[#1f222d]" />
          <Button data-testid="admin-login-submit" disabled={busy} type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-6 font-semibold">{busy ? "Giriş yapılıyor..." : "Giriş Yap"}</Button>
        </form>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <Icon size={18} className="text-blue-600" />
    </div>
    <p className="text-3xl font-extrabold text-[#1f222d] mt-2">{value ?? "—"}</p>
  </div>
);

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [seo, setSeo] = useState({});
  const [reindexing, setReindexing] = useState(false);
  const [cats, setCats] = useState([]);
  const [icons, setIcons] = useState([]);
  const [newCat, setNewCat] = useState({ name: "", icon: "Briefcase" });

  useEffect(() => { if (!loading && user?.role === "admin") setAuthed(true); }, [loading, user]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/employers").then((r) => setEmployers(r.data)).catch(() => {});
    api.get("/admin/seo").then((r) => setSeo(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/category-icons").then((r) => setIcons(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (authed) loadAll(); }, [authed, loadAll]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!authed) return <AdminLogin onDone={() => setAuthed(true)} />;

  const setJobStatus = async (id, status) => {
    await api.put(`/admin/jobs/${id}/status?status=${status}`);
    toast.success("İlan durumu güncellendi"); loadAll();
  };
  const delJob = async (id) => { await api.delete(`/admin/jobs/${id}`); toast.success("İlan silindi"); loadAll(); };
  const verifyEmp = async (id, v) => {
    await api.put(`/admin/employers/${id}/verify?verified=${v}`);
    toast.success(v ? "Marka onaylandı (mavi tik)" : "Onay kaldırıldı"); loadAll();
  };
  const delUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Kullanıcı silindi"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveSeo = async () => {
    const { data } = await api.put("/admin/seo", seo);
    setSeo(data); toast.success("SEO ayarları kaydedildi");
  };
  const reindex = async () => {
    setReindexing(true);
    try {
      const { data } = await api.post("/admin/reindex");
      toast.success(`${data.indexed_urls} URL indexlendi`, { description: "Sitemap güncellendi." });
    } finally { setReindexing(false); }
  };
  const addCat = async () => {
    if (!newCat.name.trim()) { toast.error("Kategori adı girin"); return; }
    try { await api.post("/admin/categories", newCat); toast.success("Kategori eklendi"); setNewCat({ name: "", icon: "Briefcase" }); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delCat = async (slug) => {
    try { await api.delete(`/admin/categories/${slug}`); toast.success("Kategori silindi"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tabs = [
    ["dashboard", "Özet", LayoutDashboard],
    ["jobs", "İlanlar", Briefcase],
    ["employers", "Markalar", Building2],
    ["categories", "Kategoriler", Grid3x3],
    ["users", "Kullanıcılar", Users],
    ["seo", "SEO & Sitemap", Settings],
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Yönetici Paneli | isbul.org" description="Admin paneli" />
      <div className="bg-[#1f222d] text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/isbul-logo.png" alt="isbul.org" className="h-9 w-auto object-contain bg-white rounded px-1" />
            <span className="font-semibold">Yönetici Paneli</span>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm hover:text-blue-300"><LogOut size={16} /> Çıkış</button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="space-y-1">
          {tabs.map(([k, label, Icon]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${tab === k ? "bg-blue-600 text-white" : "text-[#1f222d] hover:bg-white"}`}>
              <Icon size={18} /> {label}
            </button>
          ))}
        </aside>

        <main>
          {tab === "dashboard" && (
            <div>
              <h2 className="text-xl font-bold text-[#1f222d] mb-5">Genel Bakış</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard label="Toplam İlan" value={stats.jobs} icon={Briefcase} />
                <StatCard label="Yayındaki İlan" value={stats.approved_jobs} icon={Check} />
                <StatCard label="İş Arayanlar" value={stats.jobseekers} icon={Users} />
                <StatCard label="İşverenler" value={stats.employers} icon={Building2} />
                <StatCard label="Onaylı Markalar" value={stats.verified_employers} icon={BadgeCheck} />
                <StatCard label="Bildirimler" value={stats.notifications} icon={LayoutDashboard} />
              </div>
            </div>
          )}

          {tab === "jobs" && (
            <div>
              <h2 className="text-xl font-bold text-[#1f222d] mb-5">İlan Yönetimi ({jobs.length})</h2>
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {jobs.map((j) => (
                  <div key={j.id} data-testid={`admin-job-${j.id}`} className="flex items-center justify-between gap-3 p-4 border-b border-slate-100 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#1f222d] text-sm">{j.title}</p>
                      <p className="text-xs text-slate-500">{j.company_name} · {j.location} · {j.category_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs rounded-full px-2 py-0.5 ${j.status === "approved" ? "bg-green-50 text-green-700 border border-green-200" : j.status === "pending" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{j.status}</span>
                      {j.status !== "approved" && <button data-testid={`admin-approve-${j.id}`} onClick={() => setJobStatus(j.id, "approved")} className="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center" title="Onayla"><Check size={15} /></button>}
                      {j.status !== "rejected" && <button data-testid={`admin-reject-${j.id}`} onClick={() => setJobStatus(j.id, "rejected")} className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center" title="Reddet"><X size={15} /></button>}
                      <button data-testid={`admin-deljob-${j.id}`} onClick={() => delJob(j.id)} className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "employers" && (
            <div>
              <h2 className="text-xl font-bold text-[#1f222d] mb-5">Marka Onaylama — Mavi Tik</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {employers.map((e) => {
                  const v = e.company?.verified;
                  return (
                    <div key={e.id} data-testid={`admin-emp-${e.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-[#1f222d]">{e.company?.company_name || e.name}</h3>
                        {v && <VerifiedBadge size={16} />}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{e.email} · {e.job_count} ilan</p>
                      <Button data-testid={`admin-verify-${e.id}`} onClick={() => verifyEmp(e.id, !v)} className={`mt-4 rounded-full text-sm ${v ? "bg-slate-100 text-[#1f222d] hover:bg-slate-200" : "bg-blue-600 hover:bg-blue-700 text-white"}`}>
                        {v ? "Onayı Kaldır" : "Mavi Tik Ver"}
                      </Button>
                    </div>
                  );
                })}
                {employers.length === 0 && <p className="text-slate-500">Henüz işveren yok.</p>}
              </div>
            </div>
          )}

          {tab === "users" && (
            <div>
              <h2 className="text-xl font-bold text-[#1f222d] mb-5">Kullanıcılar ({users.length})</h2>
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 p-4 border-b border-slate-100">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#1f222d] text-sm truncate">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs rounded-full px-2 py-0.5 border ${u.role === "admin" ? "bg-purple-50 text-purple-700 border-purple-200" : u.role === "employer" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}>{u.role}</span>
                      {u.role !== "admin" && <button data-testid={`admin-deluser-${u.id}`} onClick={() => delUser(u.id)} className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center"><Trash2 size={15} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "categories" && (
            <div className="max-w-3xl">
              <h2 className="text-xl font-bold text-[#1f222d] mb-5">Kategori Yönetimi</h2>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
                <h3 className="font-semibold text-[#1f222d] mb-3">Yeni Kategori Ekle</h3>
                <label className="text-sm font-medium text-[#1f222d]">Kategori Adı</label>
                <input data-testid="newcat-name" value={newCat.name} onChange={(e) => setNewCat((c) => ({ ...c, name: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="Örn: Grafik Tasarım" />
                <label className="text-sm font-medium text-[#1f222d] block mt-4">İkon Seç</label>
                <div className="grid grid-cols-8 sm:grid-cols-10 gap-2 mt-2">
                  {icons.map((name) => {
                    const Ic = CATEGORY_ICONS[name] || CATEGORY_ICONS.Briefcase;
                    const sel = newCat.icon === name;
                    return (
                      <button key={name} data-testid={`newcat-icon-${name}`} onClick={() => setNewCat((c) => ({ ...c, icon: name }))} className={`aspect-square rounded-xl flex items-center justify-center border-2 transition-colors ${sel ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}>
                        <Ic size={18} className={sel ? "text-blue-600" : "text-slate-500"} />
                      </button>
                    );
                  })}
                </div>
                <Button data-testid="newcat-add" onClick={addCat} className="mt-5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"><Plus size={16} className="mr-1" /> Kategori Ekle</Button>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {cats.map((c) => {
                  const Ic = CATEGORY_ICONS[c.icon] || CATEGORY_ICONS.Briefcase;
                  return (
                    <div key={c.slug} data-testid={`admin-cat-${c.slug}`} className="flex items-center justify-between gap-3 p-4 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Ic size={18} className="text-blue-600" /></div>
                        <div>
                          <p className="font-semibold text-[#1f222d] text-sm">{c.name}</p>
                          <p className="text-xs text-slate-400">{c.count} ilan</p>
                        </div>
                      </div>
                      <button data-testid={`admin-delcat-${c.slug}`} onClick={() => delCat(c.slug)} className="w-9 h-9 rounded-lg bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100"><Trash2 size={15} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "seo" && (
            <div className="max-w-2xl">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-[#1f222d]">SEO & Sitemap Ayarları</h2>
                <Button data-testid="admin-reindex" onClick={reindex} disabled={reindexing} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                  <RefreshCw size={16} className={`mr-2 ${reindexing ? "animate-spin" : ""}`} /> Yeniden İndexle
                </Button>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#1f222d]">Site Başlığı</label>
                  <input data-testid="seo-title" value={seo.site_title || ""} onChange={(e) => setSeo((s) => ({ ...s, site_title: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1f222d]">Site Açıklaması (meta description)</label>
                  <textarea data-testid="seo-description" value={seo.site_description || ""} onChange={(e) => setSeo((s) => ({ ...s, site_description: e.target.value }))} rows={3} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d] resize-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1f222d]">Anahtar Kelimeler</label>
                  <input data-testid="seo-keywords" value={seo.keywords || ""} onChange={(e) => setSeo((s) => ({ ...s, keywords: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1f222d]">Google Site Doğrulama Kodu</label>
                  <input data-testid="seo-google" value={seo.google_verification || ""} onChange={(e) => setSeo((s) => ({ ...s, google_verification: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="google-site-verification içeriği" />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input data-testid="seo-autoindex" type="checkbox" checked={seo.auto_index ?? true} onChange={(e) => setSeo((s) => ({ ...s, auto_index: e.target.checked }))} className="w-5 h-5 accent-blue-600" />
                  <span className="text-sm text-[#1f222d]">Yeni ilanlar otomatik indexlensin (anında yayına alınır)</span>
                </label>
                <div className="flex items-center gap-3 pt-2">
                  <Button data-testid="seo-save" onClick={saveSeo} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 py-5 font-semibold">Kaydet</Button>
                  <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="text-sm text-blue-600 font-medium hover:underline inline-flex items-center gap-1"><Search size={14} /> sitemap.xml görüntüle</a>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
